import {
  deviceMetadataOf,
  gps9TimeMs,
  MS_PER_S,
  type RawTelemetryData,
  type GyroscopeReading,
  type AccelerometerReading,
} from '@pitbrain/shared'
import { withRunningDistance } from '../gpsReadings'
import { GOPRO } from './goProDevice'
import telemetry from './telemetry/telemetry'
import type {
  GoProRoot,
  GoProGpsSample,
  GoProGps9Sample,
  GoProGyroSample,
  GoProAcclSample,
} from './GoProTelemetryTypes'

interface NormalizedGpsSample {
  latitude: number
  longitude: number
  altitude: number
  speedMs: number
  date: string
}

const normalizeGps5 = (s: GoProGpsSample): NormalizedGpsSample | null => {
  if (s['GPS (Lat.) [deg]'] == null || s['GPS (Long.) [deg]'] == null)
    return null
  return {
    latitude: s['GPS (Lat.) [deg]'],
    longitude: s['GPS (Long.) [deg]'],
    altitude: s['GPS (Alt.) [m]'],
    speedMs: s['GPS (2D speed) [m/s]'],
    date: s.date,
  }
}

const GPS9_MIN_FIX = 2
const GPS9_MAX_DOP = 5

const normalizeGps9 = (s: GoProGps9Sample): NormalizedGpsSample | null => {
  if (s['GPS (Lat.) [deg]'] == null || s['GPS (Long.) [deg]'] == null)
    return null
  const fix = s['GPS (fix) [deg]']
  const dop = s['GPS (DOP) [deg]']
  if (fix != null && fix < GPS9_MIN_FIX) return null
  if (dop != null && dop > GPS9_MAX_DOP) return null
  return {
    latitude: s['GPS (Lat.) [deg]'],
    longitude: s['GPS (Long.) [deg]'],
    altitude: s['GPS (Alt.) [m]'],
    speedMs: s['GPS (2D) [m/s]'],
    date: s.date,
  }
}

const gps9SatelliteTimeMs = (s: GoProGps9Sample): number | null => {
  const days = s['GPS (days) [deg]']
  const secs = s['GPS (secs) [s]']
  if (days == null || secs == null) return null
  return gps9TimeMs(days, secs * MS_PER_S)
}

const clockCorrectionMs = (samples: GoProGps9Sample[]): number => {
  for (const s of samples) {
    const satMs = gps9SatelliteTimeMs(s)
    if (satMs != null && s.date != null) {
      return satMs - new Date(s.date).getTime()
    }
  }
  return 0
}

export const gyroAxes = (g: GoProGyroSample) => ({
  x: g['Gyroscope (x) [rad/s]'] ?? g['(1)'] ?? Number.NaN,
  y: g['Gyroscope (y) [rad/s]'] ?? g['(2)'] ?? Number.NaN,
  z: g['Gyroscope (z) [rad/s]'] ?? g['Gyroscope [rad/s]'] ?? Number.NaN,
})

export const acclAxes = (g: GoProAcclSample) => ({
  x: g['Accelerometer (x) [m/s2]'] ?? g['(1)'] ?? Number.NaN,
  y: g['Accelerometer (y) [m/s2]'] ?? g['(2)'] ?? Number.NaN,
  z: g['Accelerometer (z) [m/s2]'] ?? g['Accelerometer [m/s2]'] ?? Number.NaN,
})

export const parseGoProToRawTelemetry = async (
  results: Array<{ rawData: any; timing: any }> // eslint-disable-line
): Promise<RawTelemetryData> => {
  const gps: GoProRoot = await telemetry(results, {
    stream: ['GPS5', 'GPS9'],
    repeatSticky: true,
    repeatHeaders: true,
    GPS5Fix: 2,
    GPS5Precision: 500,
  })
  const gyro: GoProRoot = await telemetry(results, {
    stream: 'GYRO',
    repeatSticky: true,
    repeatHeaders: true,
  })
  const accl: GoProRoot = await telemetry(results, {
    stream: 'ACCL',
    repeatSticky: true,
    repeatHeaders: true,
  })

  const isSample = (s: NormalizedGpsSample | null): s is NormalizedGpsSample =>
    s != null
  const gpsStreams = gps[1].streams
  const normalizedSamples: NormalizedGpsSample[] = gpsStreams.GPS9?.samples
    ?.length
    ? gpsStreams.GPS9.samples.map(normalizeGps9).filter(isSample)
    : (gpsStreams.GPS5?.samples ?? []).map(normalizeGps5).filter(isSample)

  const correctionMs = gpsStreams.GPS9?.samples?.length
    ? clockCorrectionMs(gpsStreams.GPS9.samples)
    : 0
  const toUtc = (date: string | Date) =>
    new Date(new Date(date).getTime() + correctionMs)

  const gpsReadings = withRunningDistance(
    normalizedSamples.map((sample) => ({
      latitude: sample.latitude,
      longitude: sample.longitude,
      altitude: sample.altitude,
      speedMs: sample.speedMs,
      timestamp: toUtc(sample.date),
    }))
  )

  const gyroReadings: GyroscopeReading[] | undefined =
    gyro[1].streams.GYRO?.samples.map((g: GoProGyroSample) => ({
      ...gyroAxes(g),
      timestamp: toUtc(g.date),
      synchronizationTime: g.cts,
      unit: 'rad/s' as const,
    }))

  const acclReadings: AccelerometerReading[] | undefined =
    accl[1].streams.ACCL?.samples.map((g: GoProAcclSample) => ({
      ...acclAxes(g),
      timestamp: toUtc(g.date),
      cts: g.cts,
      unit: 'm/s²' as const,
    }))

  return {
    gps: gpsReadings,
    gyroscope: gyroReadings,
    accelerometer: acclReadings,
    recordingStartTime: gpsReadings[0]?.timestamp,
    device: deviceMetadataOf(GOPRO),
  }
}
